import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Empty, Modal, Row, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BookOutlined, CaretRightOutlined, EditOutlined, TrophyOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { clearNoteDetailState, getNoteDetailsByInscription, NoteDetailItem } from './note.reducer';
import { MatiereBulletin } from './bulletins.types';
import { coefficientBulletinRows, format2 } from './bulletins.utils';

const { Title, Text } = Typography;

type DetailItem = {
  evaluationTitre: string;
  note: string;
  isAbsent: boolean;
  valeurNum: number | null;
  bareme: number | null;
};

type MatiereDetail = {
  matiereId: string;
  matiereNom: string;
  devoirs: DetailItem[];
  compositions: DetailItem[];
};

type Props = {
  visible: boolean;
  inscriptionId: string | null;
  periodeId: string | null;
  classeNom: string;
  periodeName: string;
  nom: string;
  prenom: string;
  matricule: string;
  matiereNames: Record<string, string>;
  isSecondaryBulletin: boolean;
  bulletinMatieres: MatiereBulletin[];
  onClose: () => void;
};

const getNoteColor = (valeur: number | null, bareme: number | null): string => {
  if (valeur === null) return '#64748b';
  const base = bareme && bareme > 0 ? bareme : 20;
  const ratio = valeur / base;
  if (ratio >= 0.7) return '#10b981';
  if (ratio >= 0.5) return '#f59e0b';
  return '#ef4444';
};

const NoteChip = ({ item }: { item: DetailItem }) => {
  const color = item.isAbsent ? '#ef4444' : getNoteColor(item.valeurNum, item.bareme);
  const label = item.isAbsent ? 'Abs' : item.valeurNum !== null ? `${item.valeurNum}${item.bareme ? `/${item.bareme}` : ''}` : '-';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '7px 12px',
        borderRadius: 10,
        background: '#fff',
        border: '1px solid #f1f5f9',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        gap: 8,
      }}
    >
      <Text style={{ fontSize: 13, color: '#334155', flex: 1 }}>{item.evaluationTitre}</Text>
      <div
        style={{
          minWidth: 48,
          textAlign: 'center',
          padding: '2px 10px',
          borderRadius: 20,
          background: `${color}18`,
          border: `1.5px solid ${color}40`,
          color,
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </div>
    </div>
  );
};

const MatiereCard = ({ detail }: { detail: MatiereDetail }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      style={{
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        background: '#fff',
      }}
    >
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%)',
          borderBottom: expanded ? '1px solid #e2e8f0' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Space size={10}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: '#0f172a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 15,
            }}
          >
            <BookOutlined />
          </div>
          <div>
            <Text strong style={{ fontSize: 14, color: '#0f172a', display: 'block', lineHeight: 1.3 }}>
              {detail.matiereNom}
            </Text>
            <Space size={6} style={{ marginTop: 2 }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>
                <EditOutlined style={{ marginRight: 3 }} />
                {detail.devoirs.length} devoir{detail.devoirs.length !== 1 ? 's' : ''}
              </span>
              <span style={{ color: '#cbd5e1', fontSize: 11 }}>·</span>
              <span style={{ fontSize: 11, color: '#64748b' }}>
                <TrophyOutlined style={{ marginRight: 3 }} />
                {detail.compositions.length} composition{detail.compositions.length !== 1 ? 's' : ''}
              </span>
            </Space>
          </div>
        </Space>

        <CaretRightOutlined
          style={{
            color: '#94a3b8',
            fontSize: 13,
            transition: 'transform 0.25s ease',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        />
      </div>

      {expanded && (
        <div style={{ padding: '14px 16px', background: '#fafbfc' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div
                style={{
                  borderRadius: 10,
                  border: '1px solid #e0e7ff',
                  background: '#fff',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '8px 12px',
                    background: 'linear-gradient(90deg, #e0e7ff 0%, #eef2ff 100%)',
                    borderBottom: '1px solid #e0e7ff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <EditOutlined style={{ color: '#4338ca', fontSize: 13 }} />
                  <Text strong style={{ color: '#1e3a8a', fontSize: 12 }}>
                    Devoirs
                  </Text>
                  <span
                    style={{
                      marginLeft: 'auto',
                      background: '#4338ca',
                      color: '#fff',
                      borderRadius: 10,
                      padding: '0 7px',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {detail.devoirs.length}
                  </span>
                </div>
                <div style={{ padding: '8px 10px', display: 'grid', gap: 5 }}>
                  {detail.devoirs.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 12, padding: '4px 2px' }}>
                      Aucun devoir
                    </Text>
                  ) : (
                    detail.devoirs.map((item, index) => <NoteChip key={`d-${index}`} item={item} />)
                  )}
                </div>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <div
                style={{
                  borderRadius: 10,
                  border: '1px solid #ede9fe',
                  background: '#fff',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '8px 12px',
                    background: 'linear-gradient(90deg, #ede9fe 0%, #f5f3ff 100%)',
                    borderBottom: '1px solid #ede9fe',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <TrophyOutlined style={{ color: '#5b21b6', fontSize: 13 }} />
                  <Text strong style={{ color: '#6d28d9', fontSize: 12 }}>
                    Compositions
                  </Text>
                  <span
                    style={{
                      marginLeft: 'auto',
                      background: '#5b21b6',
                      color: '#fff',
                      borderRadius: 10,
                      padding: '0 7px',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {detail.compositions.length}
                  </span>
                </div>
                <div style={{ padding: '8px 10px', display: 'grid', gap: 5 }}>
                  {detail.compositions.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 12, padding: '4px 2px' }}>
                      Aucune composition
                    </Text>
                  ) : (
                    detail.compositions.map((item, index) => <NoteChip key={`c-${index}`} item={item} />)
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export const NoteDetailModal = ({
  visible,
  inscriptionId,
  periodeId,
  classeNom,
  periodeName,
  nom,
  prenom,
  matricule,
  matiereNames,
  isSecondaryBulletin,
  bulletinMatieres,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(state => state.note.detailLoading);
  const noteDetailItems = useAppSelector(state => state.note.noteDetailItems);

  useEffect(() => {
    if (!visible || !inscriptionId || !periodeId) {
      dispatch(clearNoteDetailState());
      return;
    }

    if (isSecondaryBulletin) {
      dispatch(clearNoteDetailState());
      return;
    }

    dispatch(getNoteDetailsByInscription({ inscriptionId, periodeId }));
  }, [visible, inscriptionId, periodeId, isSecondaryBulletin]);

  const secondaryColumns: ColumnsType<MatiereBulletin> = useMemo(
    () => [
      {
        title: 'Matières',
        dataIndex: 'matiereNom',
        key: 'matiereNom',
        align: 'left',
        render(value: string | null) {
          return <Text strong>{value || '-'}</Text>;
        },
      },
      { title: 'C.C', dataIndex: 'moyenneDevoirs', key: 'moyenneDevoirs', align: 'center', render: format2 },
      { title: 'Comp.', dataIndex: 'moyenneComposition', key: 'moyenneComposition', align: 'center', render: format2 },
      {
        title: 'Moy./20',
        dataIndex: 'moyenneSur20',
        key: 'moyenneSur20',
        align: 'center',
        render(value: number | null) {
          const color = value === null || value === undefined ? '#64748b' : value >= 10 ? '#15803d' : '#b91c1c';
          return <Text style={{ color, fontWeight: 700 }}>{format2(value)}</Text>;
        },
      },
      { title: 'Coef.', dataIndex: 'coefficient', key: 'coefficient', align: 'center', render: (value: number | null) => value ?? '-' },
      {
        title: 'Moy.X',
        dataIndex: 'moyenneApresCoefficient',
        key: 'moyenneApresCoefficient',
        align: 'center',
        render(value: number | null) {
          return <Text strong>{format2(value)}</Text>;
        },
      },
      {
        title: 'Appréciations',
        dataIndex: 'appreciation',
        key: 'appreciation',
        align: 'left',
        render(value: string | null) {
          return value || '';
        },
      },
    ],
    []
  );

  const matiereDetails = useMemo<MatiereDetail[]>(() => {
    const detailsMap = new Map<string, MatiereDetail>();

    noteDetailItems.forEach((item: NoteDetailItem) => {
      const matiereId = item.matiereId || 'unknown';
      if (!detailsMap.has(matiereId)) {
        detailsMap.set(matiereId, {
          matiereId,
          matiereNom: matiereNames[matiereId] || 'Sans matière',
          devoirs: [],
          compositions: [],
        });
      }

      const detail = detailsMap.get(matiereId);
      if (!detail) {
        return;
      }

      const detailItem: DetailItem = {
        evaluationTitre: item.evaluationTitre || 'Évaluation',
        note: item.absent
          ? 'Abs'
          : item.valeur !== null && item.valeur !== undefined
          ? `${item.valeur}${item.bareme ? `/${item.bareme}` : ''}`
          : '-',
        isAbsent: Boolean(item.absent),
        valeurNum: item.valeur,
        bareme: item.bareme,
      };

      const evalType = (item.type || 'DEVOIR').toUpperCase();
      if (evalType === 'COMPOSITION') {
        detail.compositions.push(detailItem);
      } else {
        detail.devoirs.push(detailItem);
      }
    });

    return Array.from(detailsMap.values()).sort((a, b) => a.matiereNom.localeCompare(b.matiereNom));
  }, [matiereNames, noteDetailItems]);

  const summary = matiereDetails.reduce(
    (acc, matiere) => {
      acc.matieres += 1;
      acc.devoirs += matiere.devoirs.length;
      acc.compositions += matiere.compositions.length;
      return acc;
    },
    { matieres: 0, devoirs: 0, compositions: 0 }
  );

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      width={900}
      destroyOnClose
      title={null}
      bodyStyle={{ padding: 16 }}
      confirmLoading={loading}
      footer={<Button onClick={onClose}>Fermer</Button>}
    >
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: 16,
          marginBottom: 14,
        }}
      >
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space align="start" size={12}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#0f172a',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                }}
              >
                {(prenom?.[0] || '') + (nom?.[0] || '')}
              </div>
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {`${nom} ${prenom}`}
                </Title>
                <Text type="secondary">{`Matricule: ${matricule || '-'}`}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              <Tag color="geekblue">{`Classe: ${classeNom || '-'}`}</Tag>
              <Tag color="cyan">{`Période: ${periodeName}`}</Tag>
            </Space>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderRadius: 10 }}>
              <Text type="secondary">Matières suivies</Text>
              <Title level={5} style={{ margin: 0, color: '#0f172a' }}>
                {summary.matieres}
              </Title>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderRadius: 10 }}>
              <Text type="secondary">Notes devoirs</Text>
              <Title level={5} style={{ margin: 0, color: '#0f172a' }}>
                {summary.devoirs}
              </Title>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderRadius: 10 }}>
              <Text type="secondary">Notes compositions</Text>
              <Title level={5} style={{ margin: 0, color: '#0f172a' }}>
                {summary.compositions}
              </Title>
            </Card>
          </Col>
        </Row>
      </div>

      <div style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: 4 }}>
        {isSecondaryBulletin ? (
          bulletinMatieres.length === 0 ? (
            <Empty description="Aucun détail de note disponible" style={{ padding: 32 }} />
          ) : (
            <Table<MatiereBulletin>
              rowKey={(row, index) => `${row.matiereId}-${index}`}
              columns={secondaryColumns}
              dataSource={coefficientBulletinRows(bulletinMatieres)}
              pagination={false}
              size="small"
            />
          )
        ) : loading ? null : matiereDetails.length === 0 ? (
          <Empty description="Aucun détail de note disponible" style={{ padding: 32 }} />
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {matiereDetails.map(detail => (
              <MatiereCard key={detail.matiereId} detail={detail} />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default NoteDetailModal;
